import { List, ListItem } from 'framework7-react';
import { ComponentType, FC, SetStateAction, useMemo, useState } from 'react';

import { PublicEvent } from '@shared/types/event';
import dayjs from 'dayjs';
import { groupBy, uniqBy } from 'lodash';

import { useTopEntertainersQuery } from '@queries/EntertainerQueries';
import { FeedEvent } from '@queries/EventQueries';
import { useCurrentUser, useSearchQuery } from '@queries/UserQueries';

import { useAnalytics } from '@hooks/useAnalytics';
import { CurrentCity, useCitiesQuery } from '@hooks/useCurrentUserLocation';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import EventCard, { EventHeader, EventImage, PromoterHeader } from '@components/cards/EventCard';
import { DatePicker } from '@components/form/DatePicker';
import { FormLabel } from '@components/form/FormLabel';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { Textarea } from '@components/form/Textarea';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { Popup } from '@components/layout/Popup/Popup';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { MultiSelectField } from '@screens/entertainer/components/CitiesField';
import { SuperAdminEventCategoryToggles } from '@screens/series/components/SuperAdminEventCategoryToggles';

import { RecurringScheduleField } from './components/RecurringScheduleField';
import { SuperAdminManagementFields } from './components/SuperAdminManagementFields';

export interface SubmitEventProperties {
  eventImage: any;
  title: string;
  description: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  cityId: string;
  isRecurring: boolean;
  entertainerIds?: string[];
  tagIds?: string[];
  status?: 'approved' | 'rejected';
  recurringSchedule?: {
    type: 'weekly' | 'bi-weekly' | 'monthlyNthDay';
    endDate?: string;
  };
  venue: {
    _id?: string;
    title: string;
  };
  entertainersByInstagram?: string[];
}

interface DefaultValues {
  eventImage: string;
  title: string;
  description: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  cityId: string;
  venue: {
    _id?: string;
    title: string;
  };
  entertainers: { _id: string; title?: string }[];
  tagIds?: string[];
  recurringSchedule?: {
    type: 'weekly' | 'bi-weekly' | 'monthlyNthDay';
    endDate?: string;
  };
  recurringParentEventId?: string;
}

type ExistingEvent = PublicEvent & { tagIds?: string[] };

interface CreateUpdateEventFormProps {
  isSubmitting?: boolean;
  cityOptions: { label: string; value: string }[];
  defaultValues?: Partial<DefaultValues>;
  entertainerType?: 'dragPerformer' | 'dj' | 'comedian';
  event?: ExistingEvent;
  AdditionalFields?: ComponentType<{
    updatedEventProperties: Partial<SubmitEventProperties>;
    setUpdatedEventProperties: (
      updatedEventProperties: SetStateAction<Partial<SubmitEventProperties>>,
    ) => void;
  }>;
  hideEntertainerFields?: boolean;
  onSubmit: (payload: SubmitEventProperties) => Promise<void>;
}

const END_DATE_REQUIRED = false;

const ENTERTAINER_LABEL_RECORDS: Record<string, string> = {
  dragPerformer: 'Drag Performers',
  dj: 'DJs',
  comedian: 'Comedians',
};

export const CreateUpdateEventForm: FC<CreateUpdateEventFormProps> = ({
  isSubmitting: isSubmittingProp,
  event,
  defaultValues = {},
  entertainerType = 'dragPerformer',
  cityOptions: initialCityOptions,
  AdditionalFields,
  hideEntertainerFields,
  onSubmit,
}) => {
  const { currentUser } = useCurrentUser();
  const isEdit = Boolean(event);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { cities } = useCitiesQuery();
  const [overrideCity, setOverrideCity] = useState<CurrentCity>();
  const [updatedEventProperties, setUpdatedEventProperties] = useState<SubmitEventProperties>({
    isRecurring: false,
    ...event,
    // Only forward statuses the update endpoint accepts
    status: event?.status === 'approved' || event?.status === 'rejected' ? event.status : undefined,
    cityId: event?.cityId ?? initialCityOptions?.[0]?.value,
    entertainerIds:
      (event?.entertainers ?? defaultValues?.entertainers)?.map(({ _id }) => _id) ?? [],
    eventImage: undefined,
    ...defaultValues,
  });

  const { topEntertainersAreReady, topEntertainers = [] } = useTopEntertainersQuery(
    updatedEventProperties.cityId,
    entertainerType,
    true,
  );

  const cityOptions = useMemo(() => {
    return [
      ...(overrideCity
        ? [{ value: overrideCity._id, label: overrideCity.name ?? overrideCity._id }]
        : []),
      ...initialCityOptions,
      { value: 'override', label: 'Other' },
      { value: 'other', label: 'Custom' },
    ];
  }, [initialCityOptions, overrideCity]);

  const startDateTime = useMemo(() => {
    const startDate: string | undefined = updatedEventProperties?.startDate ?? event?.startDate;
    const startTime: string | undefined = updatedEventProperties?.startTime ?? event?.startTime;

    return `${startDate?.toString()} ${startTime?.toString()}`.trim();
  }, [event, updatedEventProperties]);

  const endDateTime = useMemo(() => {
    const endDate: string | undefined = event?.endDate ?? updatedEventProperties?.endDate;
    const endTime: string | undefined = event?.endTime ?? updatedEventProperties?.endTime;

    return `${endDate?.toString()} ${endTime?.toString()}`.trim();
  }, [event, updatedEventProperties]);

  const handleChangeImage = (updatedEventImage: string | ArrayBuffer) => {
    setUpdatedEventProperties((prevValue) => ({
      ...prevValue,
      eventImage: updatedEventImage,
    }));
  };

  const hasStart = Boolean(updatedEventProperties?.startDate && updatedEventProperties?.startTime);

  const canSubmit = Boolean(
    !isSubmitting &&
    updatedEventProperties?.title &&
    hasStart &&
    updatedEventProperties?.cityId &&
    updatedEventProperties?.venue?.title,
  );

  const handleSubmit = async () => {
    if (canSubmit) {
      setIsSubmitting(true);
      await onSubmit(updatedEventProperties);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Card>
        <ImageUploadField
          placeholder="Add an event flyer..."
          defaultValue={defaultValues?.eventImage ?? event?.img}
          onChange={handleChangeImage}
        />
      </Card>
      <div className="grid gap-2 mt-2">
        <div>
          <FormLabel text="Title *" />
          <Input
            name="title"
            defaultValue={updatedEventProperties?.title}
            onChange={(e) =>
              setUpdatedEventProperties((prevValue) => ({
                ...prevValue,
                title: e.target.value,
              }))
            }
          />
        </div>

        <div>
          <FormLabel text="Description (optional)" />
          <Textarea
            name="description"
            defaultValue={updatedEventProperties?.description}
            onChange={(updatedDescription) =>
              setUpdatedEventProperties((prevValue) => ({
                ...prevValue,
                description: updatedDescription,
              }))
            }
          />
        </div>

        <div>
          <FormLabel text="Start *" />
          <DatePicker
            showTimeSelect
            name="startDateTime"
            value={startDateTime}
            minDate={event?.startDate}
            onChange={(updatedDate) =>
              setUpdatedEventProperties((prevValue) => ({
                ...prevValue,
                startDate: dayjs(updatedDate).format('YYYY-MM-DD'),
                startTime: dayjs(updatedDate).format('HH:mm'),
              }))
            }
          />
        </div>

        <RecurringScheduleField
          eventProperties={updatedEventProperties}
          onUpdateEventProperties={(updatedProperties) => {
            setUpdatedEventProperties((prevValue) => ({
              ...prevValue,
              ...updatedProperties,
            }));
          }}
        />

        {END_DATE_REQUIRED && (
          <div>
            <FormLabel text="End *" />
            <DatePicker
              name="endDateTime"
              showTimeSelect
              value={endDateTime}
              minDate={startDateTime}
              onChange={(updatedDate) =>
                setUpdatedEventProperties((prevValue) => ({
                  ...prevValue,
                  endDate: dayjs(updatedDate).format('YYYY-MM-DD'),
                  endTime: dayjs(updatedDate).format('HH:mm'),
                }))
              }
            />
          </div>
        )}

        <div>
          <FormLabel text="City *" />
          <Select
            name="cityId"
            options={cityOptions}
            value={updatedEventProperties.cityId}
            onChange={(e) =>
              setUpdatedEventProperties((prevValue) => ({
                ...prevValue,
                cityId: e.target.value,
              }))
            }
          />
        </div>

        {updatedEventProperties?.cityId === 'override' && (
          <div>
            <MultiSelectField
              name="cityIdOverride"
              placeholder="Select a city"
              allowSearch
              defaultValue={[]}
              options={uniqBy(cities, '_id')
                .map(({ _id, name }) => ({
                  value: _id,
                  label: name ?? _id,
                }))
                .sort((a, b) => a.label.localeCompare(b.label))}
              onChange={(updatedCityIds) => {
                const [updatedCityId] = updatedCityIds ?? [];
                if (updatedCityId) {
                  setOverrideCity(cities?.find((city) => city._id === updatedCityId));
                  setUpdatedEventProperties((prevValue) => ({
                    ...prevValue,
                    cityId: updatedCityId,
                  }));
                }
              }}
            />
          </div>
        )}

        {updatedEventProperties?.cityId === 'other' && (
          <div>
            <FormLabel text="City Name *" />
            <Input
              name="cityName"
              onChange={(e) =>
                setUpdatedEventProperties((prevValue) => ({
                  ...prevValue,
                  city: {
                    name: e.target.value,
                  },
                }))
              }
            />
          </div>
        )}

        <div>
          <FormLabel text="Venue *" />
          <Input
            name="venueTitle"
            defaultValue={updatedEventProperties?.venue?.title}
            onChange={(e) =>
              setUpdatedEventProperties((prevValue) => ({
                ...prevValue,
                venue: {
                  title: e.target.value,
                },
              }))
            }
          />
        </div>

        {!hideEntertainerFields && (
          <>
            {topEntertainersAreReady ? (
              <>
                <MultiSelectField
                  label={ENTERTAINER_LABEL_RECORDS[entertainerType] ?? 'Drag Performers'}
                  name="entertainerIds"
                  allowSearch
                  defaultValue={updatedEventProperties?.entertainerIds ?? []}
                  options={uniqBy(
                    [
                      ...topEntertainers,
                      ...(event?.entertainers ?? defaultValues?.entertainers ?? []),
                    ],
                    '_id',
                  )
                    .map(({ _id, title }) => ({
                      value: _id,
                      label: title ?? _id,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))}
                  onChange={(updatedEntertainerIds) => {
                    setUpdatedEventProperties((prevValue) => ({
                      ...prevValue,
                      entertainerIds: updatedEntertainerIds,
                    }));
                  }}
                />
                {currentUser?.isSuperAdmin && (
                  <div className="d-grid gap-2">
                    {updatedEventProperties?.entertainersByInstagram?.map((instagram, index) => (
                      <div key={`${index}`}>
                        <div className="d-flex align-items-center gap-2">
                          <div className="w-100">
                            <Input
                              type="text"
                              placeholder="eg. @dragqueen.handle"
                              value={instagram}
                              onChange={(e) =>
                                setUpdatedEventProperties((prevValue) => {
                                  const entertainersByInstagram =
                                    prevValue?.entertainersByInstagram ?? [];
                                  entertainersByInstagram[index] = e.target.value;

                                  return {
                                    ...prevValue,
                                    entertainersByInstagram,
                                  };
                                })
                              }
                            />
                          </div>
                          {instagram && (
                            <IconButton
                              variant="smooth"
                              color="danger"
                              onClick={() => {
                                setUpdatedEventProperties((prevValue) => {
                                  const entertainersByInstagram = (
                                    prevValue?.entertainersByInstagram ?? []
                                  ).filter((value) => value !== instagram);

                                  return {
                                    ...prevValue,
                                    entertainersByInstagram,
                                  };
                                });
                              }}
                            >
                              <FontAwesomeIcon name="times" />
                            </IconButton>
                          )}
                        </div>
                        {event?.entertainerInstagramDetails?.[instagram] && (
                          <div className="mt-1 px-3">
                            <div>
                              <strong>
                                {event.entertainerInstagramDetails[instagram].fullName}
                              </strong>
                            </div>
                            <div>{event.entertainerInstagramDetails[instagram].biography}</div>
                          </div>
                        )}
                      </div>
                    ))}
                    <Button
                      variant="smooth"
                      color="neutral"
                      roundness="rounded"
                      onClick={() =>
                        setUpdatedEventProperties((prevValue) => {
                          const entertainersByInstagram = [
                            ...(prevValue?.entertainersByInstagram ?? []),
                            '',
                          ];

                          return {
                            ...prevValue,
                            entertainersByInstagram,
                          };
                        })
                      }
                    >
                      Add By Instagram
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Skeleton height={64} />
            )}
          </>
        )}

        {AdditionalFields && (
          <AdditionalFields
            updatedEventProperties={updatedEventProperties}
            setUpdatedEventProperties={setUpdatedEventProperties}
          />
        )}

        {currentUser?.isSuperAdmin && event?._id && (
          <SuperAdminManagementFields eventId={event._id} />
        )}

        {/* <div>
          <FormLabel text="Drag Performers" />
          Request from local queens
          Multiselect
          <Input
            defaultValue={event?.venue?.title}
            onChange={(e) =>
              setUpdatedEventProperties((prevValue) => ({
                ...prevValue,
                venue: {
                  title: e.target.value,
                },
              }))
            }
          />
        </div> */}
      </div>
      <div className="mt-4">
        <Button
          roundness="rounded"
          disabled={!canSubmit || isSubmitting || isSubmittingProp}
          onClick={handleSubmit}
        >
          {isSubmitting || isSubmittingProp
            ? 'Saving...'
            : isEdit
              ? 'Update Event'
              : 'Create Event'}
        </Button>
      </div>
    </>
  );
};

const VIEW_MODE = 'list' as 'cards' | 'list';

const ExistingEventListContent: FC<{
  events: FeedEvent[];
  onSelectExistingEvent?: { text: string; action: (existingEventId: string) => void };
}> = ({ events, onSelectExistingEvent }) => {
  const { openConfirmDialog } = useDialog();

  const handleSelectExistingEvent = (event: FeedEvent) => {
    openConfirmDialog({
      title: 'Select Event',
      text: `Are you sure you want to add yourself to ${event.title}?`,
      onConfirm: () => {
        onSelectExistingEvent?.action(event._id);
      },
    });
  };

  const uniqueEvents = uniqBy(events, (event) => `${event.title}-${event.timeDetails}`);
  const toolbar = (
    <>
      <TitleToolbar
        className="mb-0"
        text={`${dayjs(uniqueEvents[0].startDate).format('MMMM Do')}`}
      />
      <p className="mt-0">Tap "Select" on your show — it goes straight onto your calendar.</p>
    </>
  );

  if (VIEW_MODE === 'cards') {
    return (
      <>
        {toolbar}
        {uniqueEvents.map((event) => (
          <Card
            key={event._id}
            className="event-card"
            onClick={() => handleSelectExistingEvent(event)}
          >
            <PromoterHeader event={event} />
            <EventHeader event={event} />
            <EventImage event={event} />
            <Card.Footer>
              <Button roundness="rounded">{onSelectExistingEvent?.text}</Button>
            </Card.Footer>
          </Card>
        ))}
      </>
    );
  }

  return (
    <>
      {toolbar}
      <List outlineIos dividersIos style={{ margin: '0 -1rem 0' }}>
        {uniqueEvents.map((event) => (
          <ListItem
            key={event._id}
            title={event.title}
            footer={event.timeDetails.split(' at ')[1]}
            after={
              (
                <Button
                  className="ms-2"
                  size="sm"
                  color="neutral"
                  variant="outlined"
                  onClick={() => handleSelectExistingEvent(event)}
                >
                  {onSelectExistingEvent?.text}
                </Button>
              ) as any
            }
          />
        ))}
      </List>
    </>
  );
};

interface CreateUpdateEventFormProps {
  cityOptions: { label: string; value: string }[];
  defaultValues?: Partial<DefaultValues>;
  entertainerType?: 'dragPerformer' | 'dj' | 'comedian';
  event?: ExistingEvent;
  onSubmit: (payload: SubmitEventProperties) => Promise<void>;
}

interface CreateUpdateEventContentProps extends CreateUpdateEventFormProps {
  initialEventDate?: string;
  onSelectExistingEvent?: { text: string; action: (existingEventId: string) => void };
}

export const CreateUpdateEventContent: FC<CreateUpdateEventContentProps> = ({
  cityOptions,
  defaultValues,
  initialEventDate,
  event,
  entertainerType,
  onSubmit,
  AdditionalFields,
  onSelectExistingEvent,
}) => {
  const { track } = useAnalytics();
  const [eventDate, setEventDate] = useState<string>(
    initialEventDate ?? dayjs().format('YYYY-MM-DD'),
  );
  const [eventCityId, setEventCityId] = useState<string>(cityOptions?.[0]?.value);
  const [shouldShowSearchResults, setShouldShowSearchResults] = useState(Boolean(initialEventDate));
  const [shouldCreateNewEvent, setShouldCreateNewEvent] = useState(false);
  const { searchResultsAreReady, searchResults } = useSearchQuery({
    startDate: eventDate,
    endDate: eventDate,
  });

  if (event || !onSelectExistingEvent) {
    return (
      <CreateUpdateEventForm
        cityOptions={cityOptions}
        defaultValues={defaultValues}
        entertainerType={entertainerType}
        event={event}
        AdditionalFields={AdditionalFields}
        onSubmit={onSubmit}
      />
    );
  }

  if (shouldCreateNewEvent) {
    return (
      <CreateUpdateEventForm
        cityOptions={cityOptions}
        entertainerType={entertainerType}
        defaultValues={{ startDate: eventDate, ...defaultValues }}
        AdditionalFields={AdditionalFields}
        onSubmit={onSubmit}
      />
    );
  }

  if (shouldShowSearchResults) {
    return (
      <>
        {searchResultsAreReady ? (
          <>
            {searchResults?.events?.length ? (
              <ExistingEventListContent
                events={searchResults.events}
                onSelectExistingEvent={onSelectExistingEvent}
              />
            ) : (
              <EmptyState
                title="No existing events"
                text="No one has shared any events for this day. Go ahead and create your own!"
              />
            )}
          </>
        ) : (
          <div className="d-grid gap-1">
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
          </div>
        )}
        <div className="mt-4">
          <Button
            roundness="rounded"
            variant="smooth"
            color="neutral"
            className="py-3"
            startIcon={<FontAwesomeIcon name="plus" style={{ color: 'var(--color-deeppurple)' }} />}
            onClick={() => {
              track('Create New Event');
              setShouldCreateNewEvent(true);
            }}
          >
            My show isn't listed
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="d-grid gap-2">
        <div>
          <p className="mt-0">{`To avoid shows being added more once, let's do a quick search!`}</p>
        </div>
        <div>
          <FormLabel text="Start *" />
          <DatePicker
            name="eventDate"
            value={eventDate}
            onChange={(updatedDate) => setEventDate(dayjs(updatedDate).format('YYYY-MM-DD'))}
          />
        </div>
        <div>
          <FormLabel text="City *" />
          <Select
            name="cityId"
            options={[...cityOptions, { label: 'Other', value: 'other' }]}
            value={eventCityId}
            onChange={(e) => setEventCityId(e.target.value)}
          />
        </div>
      </div>
      <div className="mt-4">
        {eventCityId === 'other' ? (
          <Button roundness="rounded" onClick={() => setShouldCreateNewEvent(true)}>
            Continue
          </Button>
        ) : (
          <Button roundness="rounded" onClick={() => setShouldShowSearchResults(true)}>
            Search Existing Events
          </Button>
        )}
      </div>
    </>
  );
};

interface CreateEditEventPopupProps extends CreateUpdateEventContentProps {
  isLoading?: boolean;
  isVisible: boolean;
  onClose: () => void;
}

export const CreateEditEventPopup: FC<CreateEditEventPopupProps> = ({
  isLoading,
  isVisible,
  onClose,
  ...contentProps
}) => {
  const { track } = useAnalytics();

  const handleClose = () => {
    track('Close Create Edit Event Popup');
    onClose();
  };

  return (
    <Popup
      isVisible={isVisible}
      onClose={handleClose}
      height="full"
      title={Boolean(contentProps?.event) ? 'Edit Event' : 'Add Event'}
    >
      <Popup.Content>
        {isVisible &&
          (!isLoading ? <CreateUpdateEventContent {...contentProps} /> : <Skeleton height={400} />)}
      </Popup.Content>
    </Popup>
  );
};
